import { z } from 'zod';
import { UserRole, SubscriptionPlan } from './types';

// ===== SCHEMAS UTILISATEURS =====
export const UserLoginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(3, 'Mot de passe requis'),
});

export const UserCreateSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string().min(2, 'Nom requis'),
  email: z.string().email('Email invalide'),
  role: z.nativeEnum(UserRole),
  avatar: z.string().optional(),
});

export type UserLogin = z.infer<typeof UserLoginSchema>;
export type UserCreate = z.infer<typeof UserCreateSchema>;

// ===== SCHEMAS LEADS =====
export const LeadCreateSchema = z.object({
  name: z.string().min(2, 'Nom du prospect requis'),
  email: z.string().email('Email invalide'),
  phone: z.string().min(5, 'Téléphone invalide'),
  status: z.enum(['new', 'contacted', 'qualified', 'lost', 'won']),
  companyId: z.string(),
  assignedTo: z.string(),
  additionalInfo: z.string().optional(),
  potentialRevenue: z.string().optional(),
  isFavorite: z.boolean().optional().default(false),
});

export const LeadUpdateSchema = LeadCreateSchema.partial();

export type LeadCreate = z.infer<typeof LeadCreateSchema>;
export type LeadUpdate = z.infer<typeof LeadUpdateSchema>;

// ===== SCHEMAS ENTREPRISES =====
export const CompanyCreateSchema = z.object({
  name: z.string().min(2, 'Nom entreprise requis'),
  plan: z.nativeEnum(SubscriptionPlan),
  userLimit: z.number().positive(),
  activeLeads: z.number().nonnegative(),
  status: z.enum(['active', 'suspended']),
  currency: z.string().length(3).optional().default('EUR'),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional(),
});

export type CompanyCreate = z.infer<typeof CompanyCreateSchema>;

// ===== SCHEMAS COMMENTAIRES =====
export const CommentCreateSchema = z.object({
  leadId: z.string(),
  userId: z.string(),
  userName: z.string().min(2),
  content: z.string().min(1, 'Commentaire vide').max(1000),
});

export type CommentCreate = z.infer<typeof CommentCreateSchema>;

// ===== VALIDATION API RESPONSES =====
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.any().optional(),
  message: z.string().optional(),
  error: z.string().optional(),
});

// Fonction helper pour valider et logger erreurs
export const validateData = <T,>(schema: z.ZodType<T>, data: unknown, context: string): T => {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join('; ');
      throw new Error(`[${context}] Validation échouée: ${issues}`);
    }
    throw error;
  }
};

export const validateOptional = <T,>(schema: z.ZodType<T>, data: unknown): T | null => {
  try {
    return schema.parse(data);
  } catch {
    return null;
  }
};
